import {
  FulfillmentOrderBy,
  ListRequest,
  useRelationshipsFulfillmentListQuery,
} from "@/services/backend/relationships/api/fulfillment_service";
import { TableCard } from "@/shared/components/tables/table-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { formatDate } from "@/shared/lib/utilities/date";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { DateBetweenFilter } from "@/shared/components/filter/date-between-filter";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { DeleteFulfillmentDialog } from "@/routes/customers/[customerId]/relationships/[relationshipId]/commitments/[commitmentId]/_components/delete-fulfillment-dialog";
import { DocumentLink } from "@/routes/_components/links/document-link";

export function FulfillmentsTable({
  request,
  onRequestChange,
  hideBorder = false,
  hidePagination = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  hideBorder?: boolean;
  hidePagination?: boolean;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useRelationshipsFulfillmentListQuery(request);

  const emptyList = !isLoading && !error && list!.fulfillments.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Arbeitsschein")}</TableHead>
              <TableHead>{t("Geplante Woche")}</TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead>{t("Erstellt am")}</TableHead>
              <TableHead>{t("Bearbeitet am")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={5} />}
            {error && <TableRtkErrorRow error={error} colSpan={5} />}
            {emptyList && <TableEmptyRow colSpan={5} />}
            {list?.fulfillments.map((fulfillment) => (
              <TableRow key={fulfillment.id}>
                <TableCell className="py-0">
                  <DocumentLink
                    documentId={fulfillment.documentId!}
                    documentKind={fulfillment.documentKind!}
                    serviceKind={fulfillment.serviceKind}
                    processId={fulfillment.processId}
                  />
                </TableCell>
                <TableCell>{fulfillment.plannedDate}</TableCell>
                <TableCell>{t(fulfillment.state)}</TableCell>
                <TableCell>{formatDate(fulfillment.createdAt)}</TableCell>
                <TableCell>{formatDate(fulfillment.updatedAt)}</TableCell>
                <TableCell>
                  <DeleteFulfillmentDialog fulfillment={fulfillment} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) => {
            onRequestChange({ ...request, limit, offset });
          }}
          className="ml-auto"
        />
      )}
    </div>
  );
}

export function FulfillmentsTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <DateBetweenFilter
        label={t("Woche")}
        filter={request.plannedDate}
        onFilterChange={(plannedDate) =>
          onRequestChange({ ...request, plannedDate })
        }
      />
      <OrderBySelect<FulfillmentOrderBy>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Woche"), value: "plannedDate" },
          { label: t("State"), value: "state" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Bearbeitet am"), value: "updatedAt" },
        ]}
      />
    </div>
  );
}
